import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Loader2,
  PlayCircle,
  AlertTriangle,
  LinkIcon,
  FileText,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Mail,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const reasonLabel: Record<string, string> = {
  expired_no_replacement: "Expired (no replacement)",
  outdated_30d: "Outdated > 30 days",
  closing_soon: "Closing soon",
  broken_link: "Broken link",
  missing_data: "Missing data",
  other: "Other",
};

const reasonColor: Record<string, string> = {
  expired_no_replacement: "bg-red-100 text-red-800 border-red-200",
  outdated_30d: "bg-amber-100 text-amber-800 border-amber-200",
  closing_soon: "bg-orange-100 text-orange-800 border-orange-200",
  broken_link: "bg-rose-100 text-rose-800 border-rose-200",
  missing_data: "bg-slate-100 text-slate-800 border-slate-200",
  other: "bg-gray-100 text-gray-800 border-gray-200",
};

type FlagStatusFilter = "open" | "resolved" | "dismissed" | "all";

const flagStatusOptions: { value: FlagStatusFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "dismissed", label: "Dismissed" },
  { value: "all", label: "All" },
];

export function MaintenancePanel() {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const [flagStatusFilter, setFlagStatusFilter] =
    useState<FlagStatusFilter>("open");

  const { data: reports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ["maintenance-reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance_reports")
        .select("*")
        .order("run_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
  });

  const { data: flags = [], isLoading: flagsLoading } = useQuery({
    queryKey: ["flagged-items", flagStatusFilter],
    queryFn: async () => {
      let q = supabase
        .from("flagged_items")
        .select("*, programs(title)")
        .order("created_at", { ascending: false })
        .limit(100);
      if (flagStatusFilter !== "all") {
        q = q.eq("status", flagStatusFilter);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  const { data: brokenLinks = [], isLoading: linksLoading } = useQuery({
    queryKey: ["broken-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("broken_links")
        .select("*, programs(title)")
        .eq("status", "broken")
        .order("checked_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  // Count of open flags (for summary card) — independent of filter
  const { data: openFlagsCount = 0 } = useQuery({
    queryKey: ["flagged-items-open-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("flagged_items")
        .select("*", { count: "exact", head: true })
        .eq("status", "open");
      if (error) throw error;
      return count ?? 0;
    },
  });

  const resolveFlag = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "resolved" | "dismissed";
    }) => {
      const { error } = await supabase
        .from("flagged_items")
        .update({ status, resolved_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Flag updated");
      qc.invalidateQueries({ queryKey: ["flagged-items"] });
      qc.invalidateQueries({ queryKey: ["flagged-items-open-count"] });
    },
    onError: () => toast.error("Failed to update flag"),
  });

  const resolveLink = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("broken_links")
        .update({ status: "resolved", resolved_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Link marked resolved");
      qc.invalidateQueries({ queryKey: ["broken-links"] });
    },
  });

  const runScan = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "weekly-content-scan"
      );
      if (error) throw error;
      toast.success(
        `Scan complete: ${data?.pages_scanned ?? 0} programs scanned, ${
          data?.pages_flagged ?? 0
        } flags`
      );
      qc.invalidateQueries({ queryKey: ["maintenance-reports"] });
      qc.invalidateQueries({ queryKey: ["flagged-items"] });
      qc.invalidateQueries({ queryKey: ["broken-links"] });
    } catch (e) {
      console.error(e);
      toast.error("Scan failed. See console for details.");
    } finally {
      setRunning(false);
    }
  };

  const latest = reports[0];
  const openBroken = brokenLinks.length;

  return (
    <div className="space-y-6">
      {/* Header / Run Now */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Content Maintenance
          </h2>
          <p className="text-sm text-muted-foreground">
            Automated weekly scan runs every Sunday at 02:00 UTC.
          </p>
        </div>
        <Button onClick={runScan} disabled={running} className="gap-2">
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PlayCircle className="h-4 w-4" />
          )}
          Run scan now
        </Button>
      </div>

      {/* Email DNS notice — non-blocking */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="p-4 flex gap-3">
          <Mail className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1.5 flex-1">
            <p className="font-medium text-amber-900">
              Email notifications: DNS pending for{" "}
              <code className="font-mono">notify.cadscon.in</code>
            </p>
            <p className="text-amber-800">
              Maintenance email code is deployed and ready. Emails will start
              sending automatically once DNS verification completes for the
              sender subdomain.
            </p>
            <p className="text-amber-800">
              <span className="font-medium">To fix:</span> open{" "}
              <span className="font-mono">Cloud → Emails → Manage Domains</span>{" "}
              and click <span className="font-medium">Rerun Setup</span> on{" "}
              <code className="font-mono">cadscon.in</code>. The dialog will
              show the exact <span className="font-mono">NS</span> records
              (typically two entries pointing to{" "}
              <code className="font-mono">ns3.lovable.cloud</code> and{" "}
              <code className="font-mono">ns4.lovable.cloud</code>) that must
              be added at your domain registrar for{" "}
              <code className="font-mono">notify.cadscon.in</code>. Then click{" "}
              <span className="font-medium">Verify Domain</span>.
            </p>
            <p className="text-xs text-amber-700">
              The rest of the app continues to work normally while DNS
              propagates (up to 72 hours).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          icon={<FileText className="h-4 w-4" />}
          label="Last scan"
          value={
            latest
              ? new Date(latest.run_at).toLocaleString("en-IN", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
              : "Never"
          }
          sub={
            latest
              ? `${latest.pages_scanned} scanned · ${latest.pages_updated} updated`
              : "Click 'Run scan now'"
          }
        />
        <SummaryCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Open flags"
          value={String(openFlagsCount)}
          sub="Items needing review"
          tone={openFlagsCount > 0 ? "warn" : "ok"}
        />
        <SummaryCard
          icon={<LinkIcon className="h-4 w-4" />}
          label="Broken links"
          value={String(openBroken)}
          sub="Detected, not resolved"
          tone={openBroken > 0 ? "warn" : "ok"}
        />
      </div>

      <Tabs defaultValue="flags">
        <TabsList>
          <TabsTrigger value="flags">Flagged ({openFlagsCount})</TabsTrigger>
          <TabsTrigger value="links">Broken links ({openBroken})</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="flags" className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Info className="h-3 w-3" /> Filter:
            </span>
            {flagStatusOptions.map((o) => (
              <Button
                key={o.value}
                size="sm"
                variant={flagStatusFilter === o.value ? "default" : "outline"}
                onClick={() => setFlagStatusFilter(o.value)}
                className="h-7 px-3 text-xs"
              >
                {o.label}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground ml-auto">
              Showing {flags.length}
            </span>
          </div>
          {flagsLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto my-12" />
          ) : flags.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-12 w-12 text-emerald-500/70" />}
              title="All clear"
              text={
                flagStatusFilter === "open"
                  ? "No open flags. Run a scan or wait for Sunday's run."
                  : `No ${flagStatusFilter === "all" ? "" : flagStatusFilter + " "}flags found.`
              }
            />
          ) : (
            <div className="space-y-2">
              {flags.map((f) => (
                <Card key={f.id}>
                  <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge
                          className={reasonColor[f.reason] ?? reasonColor.other}
                        >
                          {reasonLabel[f.reason] ?? f.reason}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {f.status}
                        </Badge>
                        {f.programs?.title && (
                          <span className="text-sm font-medium">
                            {f.programs.title}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(f.created_at).toLocaleDateString("en-IN")}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground break-words">
                        {f.details}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {f.status === "open" && (
                        <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() =>
                          resolveFlag.mutate({ id: f.id, status: "resolved" })
                        }
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Resolve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1.5"
                        onClick={() =>
                          resolveFlag.mutate({ id: f.id, status: "dismissed" })
                        }
                      >
                        <XCircle className="h-4 w-4" />
                        Dismiss
                      </Button>
                        </>
                      )}
                      {f.status !== "open" && f.resolved_at && (
                        <span className="text-xs text-muted-foreground self-center">
                          {new Date(f.resolved_at).toLocaleDateString("en-IN")}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          {linksLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto my-12" />
          ) : brokenLinks.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-12 w-12 text-emerald-500/70" />}
              title="No broken links"
              text="All external links validated successfully."
            />
          ) : (
            <div className="space-y-2">
              {brokenLinks.map((l) => (
                <Card key={l.id}>
                  <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="destructive">
                          {l.http_status ?? "ERR"}
                        </Badge>
                        {l.programs?.title && (
                          <span className="text-sm font-medium">
                            {l.programs.title}
                          </span>
                        )}
                      </div>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline break-all inline-flex items-center gap-1"
                      >
                        {l.url}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                      {l.error_message && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {l.error_message}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resolveLink.mutate(l.id)}
                      className="gap-1.5 shrink-0"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Mark fixed
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          {reportsLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto my-12" />
          ) : reports.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-12 w-12 text-muted-foreground" aria-hidden="true" />}
              title="No reports yet"
              text="Run a scan to generate the first report."
            />
          ) : (
            <div className="space-y-2">
              {reports.map((r) => (
                <Card key={r.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {new Date(r.run_at).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </CardTitle>
                    <CardDescription>
                      Triggered by {r.triggered_by}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    <Stat label="Scanned" value={r.pages_scanned} />
                    <Stat label="Updated" value={r.pages_updated} />
                    <Stat label="Flagged" value={r.pages_flagged} />
                    <Stat label="Closing soon" value={r.closing_soon_count} />
                    <Stat label="Broken links" value={r.broken_links_count} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
  tone = "ok",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "warn";
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          {icon}
          {label}
        </div>
        <div
          className={`text-2xl font-bold ${
            tone === "warn" ? "text-amber-600" : "text-foreground"
          }`}
        >
          {value}
        </div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold text-foreground">{value}</div>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="text-center py-12">
      <div className="flex justify-center mb-3">{icon}</div>
      <p className="font-medium text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}